import type { DataTableQueryParams } from '~/components/DataTable/DataTable'
import {
  applyIntersectedFilterToQuery,
  getIntersectedBarAndColumnFilter,
  parseCsvFilterValue
} from '~/components/DataTable/queryFilterHelpers'
import type {
  FilterDefinition,
  FiltersState
} from '~/components/Filters/FilterBar/types'
import { resolveFilters } from '~/components/Filters/FilterBar/utils'
import { parseProximity } from '~/domains/companies/proximity'

const SORT_FIELD_MAP: Record<string, string> = {
  name: 'name',
  lastActionAt: 'lastActionAt'
}

const TEXT_FILTER_PARAM_MAP: Record<string, string> = {
  name: 'name',
  registrationNumber: 'registrationNumber',
  archived: 'archived',
  lastActionAfter: 'lastActionAt[after]',
  lastActionBefore: 'lastActionAt[before]'
}

const CSV_FILTER_PARAM_MAP: Record<string, string> = {
  segmentations: 'segmentations',
  linkedUsers: 'linkedUsers',
  agency: 'users.agencies'
}

const INTERSECTED_FILTERS = [
  { barKey: 'status', columnId: 'companyStatus', param: 'companyStatus' },
  { barKey: 'potential', columnId: 'potential', param: 'potential' },
  { barKey: 'sector', columnId: 'sector', param: 'sector' }
]

const COLUMN_FILTER_PARAM_MAP: Record<string, string> = {
  name: 'name',
  city: 'address.city',
  lastActionAt: 'lastActionAt[after]'
}

export const appendContactCriteria = (
  queryParams: URLSearchParams,
  filters: FiltersState
) => {
  const contactTypes = parseCsvFilterValue(filters.contactTypes)
  if (contactTypes.length > 0) {
    queryParams.set('contactTypes', contactTypes.join(','))
  }

  const qualifications = parseCsvFilterValue(filters.qualifications)
  if (qualifications.length > 0) {
    queryParams.set('contactQualifications', qualifications.join(','))
  }

  const proximity = parseProximity(filters.near)
  if (proximity) {
    queryParams.set('near[lat]', String(proximity.lat))
    queryParams.set('near[lng]', String(proximity.lng))
    queryParams.set('near[radius]', String(proximity.radius))
  }
}

export const buildCompanyListSearch = (
  {
    page = 1,
    itemsPerPage = 20,
    sorting = [],
    columnFilters = []
  }: DataTableQueryParams,
  barFilters: FiltersState,
  filterDefinitions?: FilterDefinition[]
): string => {
  const filters = filterDefinitions
    ? resolveFilters(barFilters, filterDefinitions)
    : barFilters

  const queryParams = new URLSearchParams()
  queryParams.set('page', String(page))
  queryParams.set('itemsPerPage', String(itemsPerPage))

  for (const sort of sorting) {
    const field = SORT_FIELD_MAP[sort.id]
    if (field) {
      queryParams.set(`order[${field}]`, sort.desc ? 'desc' : 'asc')
    }
  }

  for (const [key, param] of Object.entries(TEXT_FILTER_PARAM_MAP)) {
    if (filters[key]) {
      queryParams.set(param, filters[key])
    }
  }

  for (const [key, param] of Object.entries(CSV_FILTER_PARAM_MAP)) {
    const values = parseCsvFilterValue(filters[key])
    if (values.length > 0) {
      queryParams.set(param, values.join(','))
    }
  }

  for (const { barKey, columnId, param } of INTERSECTED_FILTERS) {
    applyIntersectedFilterToQuery(
      queryParams,
      param,
      getIntersectedBarAndColumnFilter({
        barFilters: filters,
        barKey,
        columnFilters,
        columnId
      })
    )
  }

  for (const filter of columnFilters) {
    const param = COLUMN_FILTER_PARAM_MAP[filter.id]
    if (param && filter.value) {
      queryParams.set(param, String(filter.value))
    }
  }

  appendContactCriteria(queryParams, filters)

  return queryParams.toString()
}

export const buildCompanyContactsSearch = (
  companyId: number,
  barFilters: FiltersState
): string => {
  const queryParams = new URLSearchParams()
  queryParams.set('company.id', String(companyId))
  appendContactCriteria(queryParams, barFilters)

  return queryParams.toString()
}

export const buildCompanyContactIdsSearch = (
  dataTableParams: DataTableQueryParams,
  barFilters: FiltersState,
  filterDefinitions?: FilterDefinition[]
): string => {
  const queryParams = new URLSearchParams(
    buildCompanyListSearch(dataTableParams, barFilters, filterDefinitions)
  )
  queryParams.delete('page')
  queryParams.delete('itemsPerPage')

  return queryParams.toString()
}
